import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useClient } from "@/context/ClientContext";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Globe, User, Sparkles, ShieldCheck, Building2, Instagram, Loader2, Plug, PlugZap, Database, Server, ShieldOff, Save, Lock, CheckCircle2, XCircle, Clock, Activity, Link as LinkIcon, Copy } from "lucide-react";

export default function Settings() {
  const { user, logout } = useAuth();
  const { activeClient, activeId, refresh } = useClient();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ products: 0 });
  const [igId, setIgId] = useState("");
  const [igToken, setIgToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [privacy, setPrivacy] = useState(null);
  const [retMedia, setRetMedia] = useState(365);
  const [retPosts, setRetPosts] = useState(365);
  const [registryNotes, setRegistryNotes] = useState("");
  const [savingPrivacy, setSavingPrivacy] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [igConfigured, setIgConfigured] = useState(false);
  const [checking, setChecking] = useState(false);
  const [health, setHealth] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [generatingLink, setGeneratingLink] = useState(false);
  const [metaStatus, setMetaStatus] = useState(null);
  const [pubStatus, setPubStatus] = useState(null);
  const [togglingPub, setTogglingPub] = useState(false);

  const loadPubStatus = () =>
    api.get("/publishing/status").then(({ data }) => setPubStatus(data)).catch(() => {});

  useEffect(() => {
    loadPubStatus();
  }, []);

  const togglePauseAll = async (pause) => {
    setTogglingPub(true);
    try {
      await api.post(pause ? "/publishing/pause-all" : "/publishing/resume-all");
      await loadPubStatus();
      toast[pause ? "warning" : "success"](
        pause ? "Toutes les publications sont suspendues" : "Publications réactivées"
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setTogglingPub(false);
  };

  const toggleBrandPause = async (pause) => {
    if (!activeId) return;
    setTogglingPub(true);
    try {
      await api.post(`/clients/${activeId}/${pause ? "pause-publishing" : "resume-publishing"}`);
      await loadPubStatus();
      refresh();
      toast[pause ? "warning" : "success"](
        pause ? `Publications suspendues pour ${activeClient?.name || "cette marque"}` : "Publications réactivées pour cette marque"
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setTogglingPub(false);
  };

  useEffect(() => {
    api.get("/instagram/integration-status").then(({ data }) => setMetaStatus(data)).catch(() => {});
  }, []);

  const generateLink = async () => {
    if (!activeId) return;
    setGeneratingLink(true);
    try {
      const { data } = await api.post("/instagram/connect-link", { client_id: activeId, expires_hours: 72 });
      setLinkUrl(data.url);
      toast.success("Lien de connexion créé (valable 72 h)");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setGeneratingLink(false);
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(linkUrl);
    toast.success("Lien copié");
  };

  const checkConnection = async () => {
    if (!activeId) return;
    setChecking(true);
    setHealth(null);
    try {
      const { data } = await api.get("/instagram/health", { params: { client_id: activeId } });
      setHealth(data);
      if (data.can_publish) {
        toast.success(
          data.expiring_soon
            ? `Connexion OK — mais le token expire dans ${data.days_until_expiry} j, reconnecte bientôt`
            : "Connexion Instagram valide — prête à publier ✅"
        );
      } else {
        toast.error(data.error || "Connexion Instagram invalide");
      }
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setChecking(false);
  };

  useEffect(() => {
    api.get("/instagram/config").then(({ data }) => setIgConfigured(!!data.configured)).catch(() => {});
    // Résultat de retour OAuth Meta (?ig=connected|error|no_account)
    const params = new URLSearchParams(window.location.search);
    const ig = params.get("ig");
    if (ig === "connected") {
      toast.success("Compte Instagram connecté via Facebook");
      refresh();
    } else if (ig === "no_account") {
      toast.error("Aucun compte Instagram Business relié à cette Page Facebook");
    } else if (ig === "error") {
      toast.error("Échec de la connexion Instagram. Réessaie.");
    }
    if (ig) window.history.replaceState({}, "", window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectOAuth = async () => {
    if (!activeId) return;
    setSaving(true);
    try {
      const { data } = await api.get("/instagram/connect", { params: { client_id: activeId } });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!activeId) return;
    api.get("/stats", { params: { client_id: activeId } }).then(({ data }) => setStats(data)).catch(() => {});
    setIgId("");
    setIgToken("");
    setHealth(null);
  }, [activeId]);

  useEffect(() => {
    api.get("/privacy/config").then(({ data }) => {
      setPrivacy(data);
      setRetMedia(data.config.retention_media_days);
      setRetPosts(data.config.retention_posts_days);
      setRegistryNotes(data.config.registry_notes || "");
    }).catch(() => {});
  }, []);

  const savePrivacy = async () => {
    setSavingPrivacy(true);
    try {
      const { data } = await api.post("/privacy/config", {
        retention_media_days: Number(retMedia),
        retention_posts_days: Number(retPosts),
        registry_notes: registryNotes,
      });
      setPrivacy((p) => ({ ...p, config: data }));
      toast.success("Confidentialité enregistrée");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSavingPrivacy(false);
  };

  const revokeSessions = async () => {
    if (!window.confirm("Déconnecter toutes les sessions ? Tu devras te reconnecter.")) return;
    setRevoking(true);
    try {
      await api.post("/auth/revoke-sessions");
      toast.success("Toutes les sessions ont été révoquées");
      await logout();
      navigate("/login");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setRevoking(false);
    }
  };

  const connectIg = async () => {
    if (!igId.trim() || !igToken.trim())
      return toast.error("Renseigne l'ID du compte Instagram Business et le token d'accès");
    setSaving(true);
    try {
      await api.put(`/clients/${activeId}/instagram`, {
        instagram_business_account_id: igId,
        access_token: igToken,
      });
      toast.success("Compte Instagram connecté");
      setIgToken("");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const disconnectIg = async () => {
    setSaving(true);
    try {
      await api.delete(`/clients/${activeId}/instagram`);
      toast.success("Compte Instagram déconnecté");
      await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const Row = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-4 p-5 border-b border-white/10 last:border-b-0">
      <Icon className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1">
          {label}
        </p>
        <p className="text-sm break-words">{value}</p>
      </div>
    </div>
  );

  return (
    <>
      <PageHeader overline="Configuration · NATYE" title="Paramètres" />
      <div className="p-6 lg:p-8 max-w-2xl space-y-8">
        <div>
          <h2 className="font-heading text-lg font-bold tracking-tight mb-4 flex items-center gap-2">
            <ShieldOff className="h-5 w-5 text-primary" /> Arrêt d'urgence des publications
          </h2>
          <div
            className={`border rounded-sm p-5 space-y-4 ${pubStatus?.paused_global ? "border-red-500/40 bg-red-500/5" : "border-white/10 bg-secondary"}`}
            data-testid="publishing-killswitch"
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm font-semibold flex items-center gap-2">
                  {pubStatus?.paused_global ? (
                    <><XCircle className="h-4 w-4 text-red-400" /> Publications GLOBALEMENT suspendues</>
                  ) : (
                    <><CheckCircle2 className="h-4 w-4 text-primary" /> Publications actives</>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Coupe immédiatement toute publication automatique de <b>toutes les marques</b>, sans supprimer aucune donnée. Les posts programmés repartiront à la réactivation.
                </p>
              </div>
              <button
                onClick={() => togglePauseAll(!pubStatus?.paused_global)}
                disabled={togglingPub}
                data-testid="toggle-pause-all-button"
                className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-semibold transition-colors ${pubStatus?.paused_global ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-red-500 text-white hover:bg-red-600"}`}
              >
                {togglingPub ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldOff className="h-4 w-4" />}
                {pubStatus?.paused_global ? "Tout réactiver" : "Tout arrêter"}
              </button>
            </div>

            {activeId && (
              <div className="flex items-center justify-between gap-4 border-t border-white/10 pt-4">
                <div className="text-xs text-muted-foreground">
                  Suspendre uniquement <b>{activeClient?.name || "la marque active"}</b> :
                  {activeClient?.publishing_paused ? (
                    <span className="text-red-400 font-semibold"> suspendue</span>
                  ) : (
                    <span className="text-primary font-semibold"> active</span>
                  )}
                </div>
                <button
                  onClick={() => toggleBrandPause(!activeClient?.publishing_paused)}
                  disabled={togglingPub}
                  data-testid="toggle-brand-pause-button"
                  className="shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs font-semibold border border-white/20 hover:bg-white/5 transition-colors"
                >
                  {activeClient?.publishing_paused ? "Réactiver cette marque" : "Suspendre cette marque"}
                </button>
              </div>
            )}
          </div>
        </div>

        <div>
          <h2 className="font-heading text-lg font-bold tracking-tight mb-4">Mon compte</h2>
          <div className="border border-white/10 rounded-sm bg-secondary">
            <Row icon={User} label="Utilisateur" value={user?.email} />
            <Row
              icon={ShieldCheck}
              label="Rôle"
              value={user?.role === "admin" ? "Administrateur · accès complet" : "Membre"}
            />
          </div>
        </div>

        <div>
          <h2 className="font-heading text-lg font-bold tracking-tight mb-4">Marque active</h2>
          <div className="border border-white/10 rounded-sm bg-secondary">
            <Row icon={Building2} label="Marque" value={activeClient?.name || "—"} />
            <Row
              icon={Globe}
              label="Site source"
              value={activeClient?.website_url || "Non défini"}
            />
            <Row icon={Sparkles} label="Génération IA" value="Légendes + hashtags en français" />
            <Row icon={User} label="Produits importés" value={`${stats.products} visuels`} />
          </div>
        </div>

        <div>
          <h2 className="font-heading text-lg font-bold tracking-tight mb-4 flex items-center gap-2">
            <Instagram className="h-5 w-5" /> Connexion Instagram — {activeClient?.name || "—"}
          </h2>
          <div className="border border-white/10 rounded-sm bg-secondary p-5" data-testid="instagram-connect">
            {activeClient?.ig_connected ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3 pb-4 border-b border-white/10" data-testid="ig-account-card">
                  {activeClient.ig_profile_picture ? (
                    <img src={activeClient.ig_profile_picture} alt="" className="h-12 w-12 rounded-full object-cover border border-white/15" />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center">
                      <Instagram className="h-5 w-5 text-primary" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm truncate">
                        {activeClient.ig_username ? `@${activeClient.ig_username}` : `ID ${activeClient.ig_user_id}`}
                      </span>
                      {activeClient.ig_needs_reconnect ? (
                        <span className="text-[9px] font-bold uppercase tracking-wider bg-red-500/20 text-red-300 rounded-sm px-1.5 py-0.5">Reconnexion requise</span>
                      ) : (
                        <span className="text-[9px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 rounded-sm px-1.5 py-0.5">🟢 Connecté</span>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      ID {activeClient.ig_user_id}
                      {activeClient.ig_connected_at && <> · connecté le {new Date(activeClient.ig_connected_at).toLocaleDateString("fr-FR")}</>}
                    </p>
                  </div>
                </div>
                {activeClient?.ig_needs_reconnect ? (
                  <div className="border border-red-500/40 bg-red-500/10 rounded-sm p-4 space-y-3" data-testid="ig-reconnect-warning">
                    <div className="flex items-center gap-2 text-sm text-red-300 font-bold">
                      <PlugZap className="h-4 w-4" /> Connexion Instagram expirée — reconnexion requise
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Le jeton d'accès Instagram de cette marque n'est plus valide : <b>les publications
                      automatiques échouent</b> tant que tu n'as pas reconnecté le compte. Clique ci-dessous pour reconnecter.
                    </p>
                    <button
                      data-testid="ig-reconnect-button"
                      onClick={connectOAuth}
                      disabled={saving || !activeId}
                      className="flex items-center gap-2 bg-[#1877F2] text-white rounded-sm px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#1877F2]/85 transition-colors disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Instagram className="h-4 w-4" />}
                      Reconnecter Instagram
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-sm text-emerald-400">
                      <PlugZap className="h-4 w-4" /> Compte connecté (ID {activeClient.ig_user_id})
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Les posts planifiés de cette marque partiront automatiquement sur Instagram à
                      l'heure prévue. Tu peux aussi publier un post immédiatement depuis le Calendrier.
                    </p>
                  </>
                )}

                {/* Résultat du test de connexion en direct */}
                {health && (
                  <div
                    data-testid="ig-health-result"
                    className={`rounded-sm p-3 border text-xs ${
                      health.can_publish
                        ? health.expiring_soon
                          ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
                          : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                        : "border-red-500/40 bg-red-500/10 text-red-300"
                    }`}
                  >
                    {health.can_publish ? (
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
                        <div>
                          <b>Connexion valide — prête à publier</b>
                          {health.username && <> · @{health.username}</>}
                          {health.days_until_expiry != null && (
                            <div className="mt-1 flex items-center gap-1.5 text-muted-foreground">
                              <Clock className="h-3.5 w-3.5" />
                              {health.expiring_soon
                                ? `Le token expire dans ${health.days_until_expiry} jour(s) — pense à reconnecter bientôt.`
                                : `Token valide encore ~${health.days_until_expiry} jour(s) (renouvelé automatiquement).`}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2">
                        <XCircle className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>{health.error || "Connexion invalide — reconnecte le compte."}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="ig-check-connection-button"
                    onClick={checkConnection}
                    disabled={checking || !activeId}
                    className="flex items-center gap-2 border border-primary/50 text-primary rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-primary/10 transition-colors disabled:opacity-50"
                  >
                    {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Activity className="h-4 w-4" />}
                    Tester la connexion
                  </button>
                  <button
                    data-testid="ig-reconnect-oauth-button"
                    onClick={connectOAuth}
                    disabled={saving || !activeId}
                    className="flex items-center gap-2 border border-white/15 rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-white/[0.05] transition-colors disabled:opacity-50"
                  >
                    <Instagram className="h-4 w-4" /> Reconnecter
                  </button>
                  <button
                    data-testid="ig-disconnect-button"
                    onClick={disconnectIg}
                    disabled={saving}
                    className="border border-white/15 rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-white/[0.05] transition-colors disabled:opacity-50"
                  >
                    Déconnecter
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {igConfigured && (
                  <div className="space-y-3 pb-4 border-b border-white/10">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Connecte le compte Instagram <b>Business/Créateur</b> (relié à une Page Facebook)
                      en un clic via l'API officielle Meta. Aucun mot de passe Instagram n'est demandé.
                    </p>
                    <button
                      data-testid="ig-connect-oauth-button"
                      onClick={connectOAuth}
                      disabled={saving || !activeId}
                      className="flex items-center gap-2 bg-[#1877F2] text-white rounded-sm px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#1877F2]/85 transition-colors disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Instagram className="h-4 w-4" />}
                      Connecter Instagram
                    </button>
                  </div>
                )}
                <details className="group" data-testid="ig-advanced-details" open={!igConfigured}>
                  {igConfigured && (
                    <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors list-none">
                      + Méthode avancée (saisie manuelle ID + token)
                    </summary>
                  )}
                  <div className="space-y-4 pt-4">
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {igConfigured ? (
                        <>Réservé aux cas avancés : renseigne manuellement l'<b>ID du compte Instagram Business</b> et un <b>token d'accès</b> (obtenus dans ton app Meta Developer). La méthode recommandée reste le bouton « Connecter Instagram » ci-dessus.</>
                      ) : (
                        <>Connecte le compte Instagram <b>Business/Créateur</b> de la marque (relié à une Page
                        Facebook) via l'API officielle Meta. Aucun mot de passe Instagram n'est demandé.
                        Renseigne l'<b>ID du compte Instagram Business</b> et un <b>token d'accès</b> (obtenus
                        dans ton app Meta Developer). Voir la page Légal pour la conformité.</>
                      )}
                    </p>
                    <input
                      data-testid="ig-id-input"
                      value={igId}
                      onChange={(e) => setIgId(e.target.value)}
                      placeholder="ID du compte Instagram Business (ex : 178414…)"
                      className="w-full bg-transparent border border-white/15 rounded-sm px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors"
                    />
                    <input
                      data-testid="ig-token-input"
                      type="password"
                      value={igToken}
                      onChange={(e) => setIgToken(e.target.value)}
                      placeholder="Token d'accès (long-lived) Meta"
                      className="w-full bg-transparent border border-white/15 rounded-sm px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors"
                    />
                    <button
                      data-testid="ig-connect-button"
                      onClick={connectIg}
                      disabled={saving || !activeId}
                      className="flex items-center gap-2 bg-primary text-primary-foreground rounded-sm px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-primary/80 transition-colors disabled:opacity-50"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
                      Connecter Instagram
                    </button>
                  </div>
                </details>
              </div>
            )}
          </div>
        </div>

        {/* -------------------- Faire connecter par le client (lien temporaire) -------------------- */}
        {metaStatus?.app_id_configured && (
          <div className="border border-white/10 rounded-sm bg-secondary p-5" data-testid="ig-client-link">
            <h2 className="font-heading text-lg font-bold tracking-tight mb-1 flex items-center gap-2">
              <LinkIcon className="h-5 w-5 text-primary" /> Faire connecter le compte par le client
            </h2>
            <p className="text-xs text-muted-foreground leading-relaxed mb-4">
              Génère un lien sécurisé à envoyer au propriétaire de <b>{activeClient?.name || "la marque"}</b>. Il
              ouvre le lien, se connecte lui-même à Instagram (sans jamais te donner ses identifiants ni accéder à ton
              dashboard), et son compte est automatiquement associé à cette marque. Lien valable 72 h, à usage unique.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="ig-generate-link-button"
                onClick={generateLink}
                disabled={generatingLink || !activeId}
                className="flex items-center gap-2 bg-primary text-primary-foreground rounded-sm px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-primary/80 transition-colors disabled:opacity-50"
              >
                {generatingLink ? <Loader2 className="h-4 w-4 animate-spin" /> : <LinkIcon className="h-4 w-4" />}
                Créer un lien de connexion
              </button>
            </div>
            {linkUrl && (
              <div className="mt-4 flex items-center gap-2 border border-white/12 rounded-sm p-2.5" data-testid="ig-link-result">
                <input
                  readOnly
                  value={linkUrl}
                  onClick={(e) => e.target.select()}
                  className="flex-1 bg-transparent text-xs text-muted-foreground outline-none"
                />
                <button
                  data-testid="ig-copy-link-button"
                  onClick={copyLink}
                  className="shrink-0 flex items-center gap-1.5 border border-primary/50 text-primary rounded-sm px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider hover:bg-primary/10 transition-colors"
                >
                  <Copy className="h-3.5 w-3.5" /> Copier
                </button>
              </div>
            )}
          </div>
        )}

        {/* -------------------- État de l'intégration Meta -------------------- */}
        <div className="border border-white/10 rounded-sm bg-secondary p-5" data-testid="meta-integration-status">
          <h2 className="font-heading text-lg font-bold tracking-tight mb-4 flex items-center gap-2">
            <Server className="h-5 w-5" /> Intégration Meta / Instagram
          </h2>
          {metaStatus ? (
            <div className="space-y-2.5">
              {[
                ["App ID configuré", metaStatus.app_id_configured],
                ["App Secret configuré", metaStatus.app_secret_configured],
                ["Redirect URI configuré", metaStatus.redirect_uri_configured],
                ["Connexion API Meta", metaStatus.api_connection_ok],
              ].map(([label, ok]) => (
                <div key={label} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{label}</span>
                  {ok ? (
                    <span className="flex items-center gap-1.5 text-emerald-400 font-bold text-xs uppercase tracking-wider"><CheckCircle2 className="h-4 w-4" /> OK</span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-red-400 font-bold text-xs uppercase tracking-wider"><XCircle className="h-4 w-4" /> Manquant</span>
                  )}
                </div>
              ))}
              {metaStatus.redirect_uri && (
                <p className="text-[10px] text-muted-foreground pt-2 border-t border-white/10 mt-2 break-all">
                  Redirect URI : {metaStatus.redirect_uri}
                </p>
              )}
              <p className="text-[10px] text-muted-foreground">La valeur de l'App Secret n'est jamais affichée.</p>
            </div>
          ) : (
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          )}
        </div>

        <div className="border border-white/10 rounded-sm bg-primary/10 p-5">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-accent mb-2">
            Publication auto
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Les posts planifiés partent automatiquement à l'heure prévue via l'API officielle Meta, dès qu'un
            compte Instagram est connecté à la marque. Sans compte connecté, aucune publication n'est simulée :
            le post est marqué en échec et t'invite à connecter Instagram.
          </p>
        </div>

        {/* -------------------- Centre de Confidentialité (RGPD) -------------------- */}
        <div data-testid="privacy-center">
          <h2 className="font-heading text-lg font-bold tracking-tight mb-4 flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-accent" /> Centre de Confidentialité — RGPD
          </h2>

          {/* Registre / données */}
          <div className="border border-white/10 rounded-sm bg-secondary p-5 mb-4">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
              Registre des traitements
            </p>
            {privacy ? (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="privacy-counts">
                {[
                  ["Marques", privacy.counts.clients],
                  ["Produits", privacy.counts.products],
                  ["Posts", privacy.counts.posts],
                  ["Visuels IA", privacy.counts.media],
                  ["Comptes", privacy.counts.users],
                ].map(([label, val]) => (
                  <div key={label} className="border border-white/10 rounded-sm p-3 text-center">
                    <p className="font-heading text-2xl font-black">{val}</p>
                    <p className="text-[10px] tracking-[0.15em] uppercase text-muted-foreground mt-1">{label}</p>
                  </div>
                ))}
              </div>
            ) : (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>

          {/* Durées de conservation */}
          <div className="border border-white/10 rounded-sm bg-secondary p-5 mb-4">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3 flex items-center gap-2">
              <Database className="h-4 w-4" /> Durées de conservation
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] text-muted-foreground mb-1">Visuels IA (jours)</label>
                <input
                  data-testid="retention-media-input"
                  type="number"
                  min={1}
                  value={retMedia}
                  onChange={(e) => setRetMedia(e.target.value)}
                  disabled={user?.role !== "admin"}
                  className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                />
              </div>
              <div>
                <label className="block text-[11px] text-muted-foreground mb-1">Posts publiés (jours)</label>
                <input
                  data-testid="retention-posts-input"
                  type="number"
                  min={1}
                  value={retPosts}
                  onChange={(e) => setRetPosts(e.target.value)}
                  disabled={user?.role !== "admin"}
                  className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                />
              </div>
            </div>
            {user?.role === "admin" && (
              <button
                data-testid="save-retention-button"
                onClick={savePrivacy}
                disabled={savingPrivacy}
                className="mt-4 flex items-center gap-2 bg-primary text-primary-foreground rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-primary/80 transition-colors disabled:opacity-50"
              >
                {savingPrivacy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Enregistrer
              </button>
            )}
          </div>

          {/* Sous-traitants */}
          <div className="border border-white/10 rounded-sm bg-secondary p-5 mb-4">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3 flex items-center gap-2">
              <Server className="h-4 w-4" /> Sous-traitants & fournisseurs externes
            </p>
            <div className="space-y-3" data-testid="privacy-providers">
              {(privacy?.providers || []).map((p) => (
                <div key={p.name} className="border-b border-white/10 last:border-b-0 pb-3 last:pb-0">
                  <p className="text-sm font-bold">{p.name} <span className="text-muted-foreground font-normal">— {p.role}</span></p>
                  <p className="text-xs text-muted-foreground mt-0.5">{p.purpose}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    <span className="text-accent">Données :</span> {p.data}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    <span className="text-accent">Localisation :</span> {p.location}
                  </p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[11px]">
                    {p.privacy_url && p.privacy_url.startsWith("http") ? (
                      <a href={p.privacy_url} target="_blank" rel="noopener noreferrer" className="text-foreground/80 underline hover:text-accent">Politique de confidentialité</a>
                    ) : (
                      <span className="text-muted-foreground">Politique : information à vérifier</span>
                    )}
                    {p.dpa_url && p.dpa_url.startsWith("http") ? (
                      <a href={p.dpa_url} target="_blank" rel="noopener noreferrer" className="text-foreground/80 underline hover:text-accent">DPA</a>
                    ) : (
                      <span className="text-muted-foreground">DPA : information à vérifier</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Registre RGPD simplifié (éditable) */}
          <div className="border border-white/10 rounded-sm bg-secondary p-5 mb-4">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3 flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" /> Registre RGPD simplifié
            </p>
            <textarea
              data-testid="registry-notes-input"
              value={registryNotes}
              onChange={(e) => setRegistryNotes(e.target.value)}
              disabled={user?.role !== "admin"}
              rows={7}
              className="w-full bg-transparent border border-white/15 rounded-sm px-3 py-2 text-xs leading-relaxed outline-none focus:ring-1 focus:ring-primary disabled:opacity-60 resize-y"
            />
            <p className="text-[10px] text-muted-foreground mt-2">
              Fiche interne indicative — ne garantit pas à elle seule une conformité complète.
            </p>
            {user?.role === "admin" && (
              <button
                data-testid="save-registry-button"
                onClick={savePrivacy}
                disabled={savingPrivacy}
                className="mt-3 flex items-center gap-2 border border-white/15 rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-white/[0.05] transition-colors disabled:opacity-50"
              >
                {savingPrivacy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Enregistrer le registre
              </button>
            )}
          </div>

          {/* Révocation des sessions */}
          <div className="border border-white/10 rounded-sm bg-secondary p-5">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-2 flex items-center gap-2">
              <Lock className="h-4 w-4" /> Sessions & sécurité
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed mb-4">
              Révoque immédiatement tous les jetons d'accès en cours (toutes les sessions ouvertes).
              Tu seras déconnecté(e) et devras te reconnecter.
            </p>
            <button
              data-testid="revoke-sessions-button"
              onClick={revokeSessions}
              disabled={revoking}
              className="flex items-center gap-2 border border-accent/40 text-accent rounded-sm px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-accent/10 transition-colors disabled:opacity-50"
            >
              {revoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldOff className="h-4 w-4" />}
              Déconnecter toutes les sessions
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
